import flatten from 'lodash/flatten'
import React, { useCallback, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'
import { Column, SortingRule } from 'react-table'
import { isEmpty } from 'lodash'
import { faCopy, faExclamationCircle, faLink } from '@fortawesome/pro-regular-svg-icons'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import isEqual from 'lodash/isEqual'

import { useParamsToObj } from '../hooks/useParamsToObj'
import { useOffers } from '../hooks/useOffers'
import offerService from '../services/offerService'
import { queryClient } from '../utils/react-query'
import { useUser } from '../hooks/useUser'

import TableSkeleton from '../components/TableSkeleton'
import Table from '../components/Table'
import Spinner from '../components/Spinner'
import PageHeader from '../components/PageHeader'
import { Offer, OffersFilter } from '../types/offers'
import FilterDrawer from '../components/Offers/FilterDrawer'
import EmptyContent from '../components/EmptyContent'
import deepLinksService from '../services/deepLinksService'
import IconButton from '../components/IconButton'

const deepLinkQueryKeys = {
  all: ['dynamicLinks'] as const,
  offer: (offer: Pick<Offer, 'id' | 'title' | 'description' | 'image'>) =>
    [
      ...deepLinkQueryKeys.all,
      'offer',
      offer.id,
      {
        title: offer.title,
        description: offer.description,
        imageLink: offer.image?.url,
      },
    ] as const,
}

const GENERIC_ERROR_MSG = 'Something went wrong, if the error persist please contact support.'

const Offers: React.FC = () => {
  const { counter, ...urlParams } = useParamsToObj<OffersFilter>([
    'locations',
    'type',
    'cost',
    'nbClaimed',
    'customers',
    'search',
  ])

  const [filterDrawerStatus, setFilterDrawerStatus] = useState(false)
  const [orderByFilter, setOrderBy] = useState<SortingRule<Offer>[]>([])
  const user = useUser()

  const offers = useOffers(orderByFilter)
  const copyOffer = useMutation(offerService.copyOffer, {
    onSuccess: async (newOffer) => {
      await queryClient.refetchQueries(['offers'])
      navigate(`/rewards-and-offers/${newOffer.id}`)
    },
  })
  const navigate = useNavigate()
  const { ref, inView } = useInView({
    rootMargin: '100px 0px 0px 0px',
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const { hasNextPage, fetchNextPage } = offers

  const handleCopy = async (offerId: string) => {
    try {
      const { id, createdAt, ...singleOffer } = await offerService.getOne(offerId)
      await toast.promise(copyOffer.mutateAsync(singleOffer), {
        loading: 'Copying...',
        success: <b>Copied successfully!</b>,
        error: <b>Could not copy. Try again</b>,
      })
    } catch {
      toast.error(GENERIC_ERROR_MSG)
    }
  }

  const [isCopyingDeepLink, setIsCopyingDeepLink] = useState(false)
  const handleCopyDeepLink = async (offer: Pick<Offer, 'id' | 'title' | 'description' | 'image'>) => {
    setIsCopyingDeepLink(true)
    try {
      const deepLink = await queryClient.fetchQuery(
        deepLinkQueryKeys.offer(offer),
        () => deepLinksService.createToOffer(offer),
        {
          staleTime: Infinity,
          cacheTime: Infinity,
          retry: false,
        }
      )
      await navigator.clipboard.writeText(deepLink)
    } catch (error) {
      console.error(error)
      toast.error(GENERIC_ERROR_MSG)
    } finally {
      setIsCopyingDeepLink(false)
    }
  }

  const columns = React.useMemo<Column<Offer>[]>(
    () => [
      {
        id: 'title',
        Header: 'Title',
        accessor: ({ title }) => (
          <span style={{ maxWidth: 137 }} className="block truncate">
            {title}
          </span>
        ),
      },
      {
        id: 'customerName',
        Header: 'Customer',
        accessor: ({ customer }) => (
          <span style={{ maxWidth: 85 }} className="block truncate">
            {customer?.name}
          </span>
        ),
      },
      {
        id: 'firstPlaceName',
        Header: 'Location',
        accessor: ({ places }) =>
          !places || places.length === 0 ? (
            'All Locations'
          ) : (
            <span style={{ maxWidth: 184 }} className="block truncate">
              {places.map(({ name }) => name).join(', ')}
            </span>
          ),
      },
      {
        id: 'type',
        Header: 'Type',
        accessor: 'type',
      },
      {
        id: 'redeemableFrom',
        Header: 'From',
        accessor: ({ redeemableFrom }) => new Date(redeemableFrom).toLocaleDateString('en-GB'),
      },
      {
        id: 'redeemableTo',
        Header: 'To',
        accessor: ({ redeemableTo }) => new Date(redeemableTo).toLocaleDateString('en-GB'),
      },
      {
        id: 'cost',
        Header: 'Cost',
        accessor: 'cost',
      },
      {
        id: 'actions',
        Header: ' ',
        disableSortBy: true,
        accessor: (offer) => (
          <div className="flex flex-row justify-center">
            <IconButton
              onClick={() => handleCopy(offer.id)}
              actionDescription="Duplicate"
              icon={faCopy}
              disabled={isCopyingDeepLink}
            />
            <IconButton
              onClick={() => handleCopyDeepLink(offer)}
              actionDescription="Copy shareable link to offer"
              icon={faLink}
              loading={isCopyingDeepLink}
              disabled={isCopyingDeepLink}
            />
          </div>
        ),
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: ({ id }) => (
          <button
            onClick={() => {
              navigate(`/rewards-and-offers/${id}`)
            }}
            className="text-link"
          >
            View
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    const init = async () => {
      if (inView && hasNextPage) {
        await fetchNextPage()
      }
    }
    init()
  }, [inView, hasNextPage, fetchNextPage])

  // Handle react-table sorting changes (in Table.tsx)
  const handleSortByChange = useCallback(
    (orderBy: SortingRule<any>[]) => {
      if (isEqual(orderByFilter, orderBy)) return

      setOrderBy(orderBy)
    },
    [orderByFilter]
  )

  const offersData = flatten(offers.data?.pages)

  if (offers.isLoading) {
    return (
      <>
        <PageHeader
          title="Offers & Rewards"
          filterBtnAction={() => {}}
          btnAction={() => navigate('add-new')}
          btnText="New"
          urlParams={{ ...urlParams, counter }}
          searchPlaceholder="Search Offers & Rewards Name"
          nbOfExtraFilter={counter}
        />
        <TableSkeleton headers={['Title', 'Customer', 'Location', 'Type', 'From', 'To']} />
      </>
    )
  }

  return (
    <div>
      <FilterDrawer open={filterDrawerStatus} onClose={() => setFilterDrawerStatus(false)} />
      <PageHeader
        btnAction={() => navigate('add-new')}
        btnText={user.data?.type === 'CUSTOMER' && user.data?.role === 'OPERATOR' ? undefined : 'New'}
        title="Offers & Rewards"
        filterBtnAction={() => setFilterDrawerStatus(true)}
        urlParams={{ ...urlParams, counter }}
        searchPlaceholder="Search Offers & Rewards Name"
        nbOfExtraFilter={0}
      />
      {!isEmpty(urlParams) && !offersData.length ? (
        <EmptyContent
          title={`No results found`}
          description="Please change the search terms to match results."
          icon={faExclamationCircle}
          btnAction={() => {
            navigate({
              search: ``,
            })
          }}
          btnText="Reset filters"
        />
      ) : (
        <Table columns={columns} data={offersData} onSortBy={handleSortByChange} />
      )}
      {hasNextPage ? (
        <div ref={ref} className="mt-4 mb-4 flex flex-col items-center text-center">
          <Spinner className="h-8 w-8 text-gray-400" />
          <span className="text-gray-400">Loading more...</span>
        </div>
      ) : null}
    </div>
  )
}

export default Offers
